import React from "react";
import { connect } from "react-redux";
import { useGoogleLogin } from "@react-oauth/google";
import { Link } from "react-router-dom";
import cookie from "react-cookies";
import ReCAPTCHA from "react-google-recaptcha";
import store from "../../store";
import { LoginAction, googleAction } from "../../actions/user/login";
import {
  CLIENT_KEY_RECAPTCHA,
  IN_PROCESS,
  IS_LOGIN,
  PATH_COOKIE,
  TOKEN_COOKIE,
  TO_EXTERNAL_LOGIN,
  TO_LOGIN,
  TO_QUOTE_LIST,
  TO_RECOVER_PASSWORD,
  USER_COOKIE,
} from "../../helpers/constants";
import { toast, setToken, setUser } from "../../helpers/utility";
import { AuthField, AuthShell, PasswordField } from "./auth-shell";

const qs = require("qs");

const GoogleEmployeeButton = ({ pending, onStart, onSuccess, onError }) => {
  const login = useGoogleLogin({
    scope: "openid email profile",
    onSuccess: async (tokenResponse) => {
      try {
        const response = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        });
        if (!response.ok) throw new Error("Google profile request failed");
        const profile = await response.json();
        onSuccess({
          ...profile,
          googleId: profile.sub,
          imageUrl: profile.picture,
          accessToken: tokenResponse.access_token,
        });
      } catch (error) {
        onError("Google sign in could not retrieve your account profile. Try again.");
      }
    },
    onError: () => onError("Google sign in failed. Try again."),
    onNonOAuthError: () => onError("Google sign in was cancelled or blocked. Try again."),
  });

  return (
    <button
      className="google-auth-button"
      type="button"
      disabled={pending}
      onClick={() => {
        onStart();
        login();
      }}>
      <i className={`fab fa-google ${pending ? "is-loading" : ""}`} aria-hidden="true"></i>
      <span>{pending ? "Signing in with Google..." : "Sign in with Google"}</span>
      {pending && <i className="fa fa-circle-o-notch fa-spin google-button-spinner" aria-hidden="true"></i>}
    </button>
  );
};

class LoginForm extends React.Component {
  googleTimeout = null;

  state = {
    user: { email: "", password: "" },
    redirect: "",
    captchaVal: "",
    captchaError: "",
    errors: {},
    googlePending: false,
    googleError: "",
  };

  componentDidMount() {
    const queryString = qs.parse(this.props.history.location.search, { ignoreQueryPrefix: true });
    if (queryString.redirect) this.setState({ redirect: queryString.redirect });

    if (this.props.user.isLoggedIn) {
      this.props.history.push(queryString.redirect || TO_QUOTE_LIST);
    } else {
      cookie.remove(TOKEN_COOKIE, { path: PATH_COOKIE });
      cookie.remove(USER_COOKIE, { path: PATH_COOKIE });
      store.dispatch({ type: IS_LOGIN, payload: false });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.user.isLoggedIn !== prevProps.user.isLoggedIn && this.props.user.isLoggedIn) {
      if (this.props.user.user.token) {
        setToken(this.props.user.user.token);
        setUser(this.props.user.user);
      }
      this.props.history.push(this.state.redirect || TO_QUOTE_LIST);
    } else if (
      this.props.user.isLoggedIn === false &&
      this.props.user.loginMessage &&
      this.props.user.loginMessage !== prevProps.user.loginMessage
    ) {
      toast(this.props.user.loginMessage, "error");
    }
  }

  componentWillUnmount() {
    if (this.googleTimeout) clearTimeout(this.googleTimeout);
  }

  startGoogleAuth = () => {
    if (this.googleTimeout) clearTimeout(this.googleTimeout);
    this.setState({ googlePending: true, googleError: "" });
    this.googleTimeout = setTimeout(() => {
      this.setState({ googlePending: false, googleError: "Google sign in did not complete. Try again." });
    }, 60000);
  };

  finishGoogleAuth = () => {
    if (this.googleTimeout) clearTimeout(this.googleTimeout);
    this.googleTimeout = null;
  };

  handleChange = (event) => {
    const { name, value } = event.target;
    this.setState((state) => ({
      user: { ...state.user, [name]: value },
      errors: { ...state.errors, [name]: "" },
    }));
  };

  validateExternalLogin = () => {
    const errors = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.state.user.email)) errors.email = "Enter a valid email address.";
    if (!this.state.user.password) errors.password = "Enter your password.";
    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  handleOnSubmit = (event) => {
    event.preventDefault();
    if (!this.validateExternalLogin()) return;
    if (!this.state.captchaVal) {
      this.setState({ captchaError: "Confirm that you are not a robot." });
      return;
    }
    store.dispatch({ type: IN_PROCESS, payload: true });
    store.dispatch(LoginAction(this.state.user));
  };

  handleGoogleSuccess = async (profile) => {
    this.finishGoogleAuth();
    this.setState({ googlePending: true, googleError: "" });
    try {
      if (profile && profile.email && profile.email.endsWith("@intrepidcs.com")) {
        const result = await store.dispatch(googleAction(profile));
        if (!result || !result.status) {
          this.setState({ googlePending: false, googleError: result && result.msg ? result.msg : "Google sign in failed. Try again." });
        }
      } else {
        this.setState({ googlePending: false, googleError: "Use your IntrepidCS email address to sign in." });
        toast("Use your IntrepidCS email address to sign in.", "error");
      }
    } catch (error) {
      this.setState({ googlePending: false, googleError: "Google sign in failed. Try again." });
      toast("Sign in failed. Try again.", "error");
    }
  };

  renderExternalLogin() {
    const { errors } = this.state;
    return (
      <AuthShell
        eyebrow="External quotes"
        title="Welcome back"
        description="Sign in to create and manage external quote requests."
        footer={<><Link to={TO_LOGIN}>IntrepidCS employee sign in</Link></>}>
        <form className="auth-form" onSubmit={this.handleOnSubmit} noValidate>
          <AuthField label="Email address" id="external-email" name="email" type="email" autoComplete="email" value={this.state.user.email} onChange={this.handleChange} error={errors.email} />
          <PasswordField label="Password" id="external-password" name="password" autoComplete="current-password" value={this.state.user.password} onChange={this.handleChange} error={errors.password} />
          <div className="auth-form-meta">
            <span>External user account</span>
            <Link to={TO_RECOVER_PASSWORD}>Forgot password?</Link>
          </div>
          <div className="auth-captcha">
            <ReCAPTCHA
              sitekey={CLIENT_KEY_RECAPTCHA}
              onChange={(value) => this.setState({ captchaVal: value || "", captchaError: "" })}
            />
          </div>
          {this.state.captchaError && <div className="auth-captcha-error" role="alert">{this.state.captchaError}</div>}
          <button className="auth-primary" type="submit" disabled={this.props.global.inProcess}>
            {this.props.global.inProcess ? "Signing in..." : "Sign in"}
          </button>
        </form>
      </AuthShell>
    );
  }

  renderInternalLogin() {
    return (
      <AuthShell
        eyebrow="IntrepidCS"
        title="Employee sign in"
        description="Continue with your IntrepidCS Google Workspace account."
        compact
        footer={<><Link to={TO_EXTERNAL_LOGIN}>External user sign in</Link></>}>
        <div className="google-login-wrap" aria-busy={this.state.googlePending}>
          <GoogleEmployeeButton
            pending={this.state.googlePending}
            onStart={this.startGoogleAuth}
            onSuccess={this.handleGoogleSuccess}
            onError={(message) => {
              this.finishGoogleAuth();
              this.setState({ googlePending: false, googleError: message });
              toast(message, "error");
            }}
          />
        </div>
        <div className="google-auth-feedback" role="status" aria-live="polite">{this.state.googlePending ? "Complete Google authentication in the popup." : ""}</div>
        {this.state.googleError && <div className="auth-inline-error" role="alert">{this.state.googleError}</div>}
        <p className="auth-help" style={{ marginTop: 18 }}>Only active <strong>@intrepidcs.com</strong> accounts can use employee sign in.</p>
      </AuthShell>
    );
  }

  render() {
    return this.props.location.pathname === TO_EXTERNAL_LOGIN
      ? this.renderExternalLogin()
      : this.renderInternalLogin();
  }
}

const stateMap = (state) => ({ user: state.user, global: state.global });
export default connect(stateMap)(LoginForm);
